/**
 * FileUploadController — composant réutilisable d'upload de fichier
 * (EPIC-007 / 7.7).
 *
 * Gère drag-drop + sélection input file + validation MIME + taille. Émet
 * les événements `file:selected` (succès) et `file:error` (échec) consommés
 * par le controller parent (ex. documents-liste).
 *
 * Targets :
 *   - dropZone     — zone visuelle de drag-drop
 *   - fileInput    — <input type="file">
 *   - errorMessage — paragraphe pour message d'erreur (hidden par défaut)
 *
 * Values :
 *   - acceptMime   (default "application/pdf") — MIME accepté
 *   - maxBytes     (default 10485760 = 10 MB) — taille max
 *
 * Événements émis :
 *   - file:selected  detail: { file: File }
 *   - file:error     detail: { code, message }
 */

import BaseController from "./base_controller.js";

export default class FileUploadController extends BaseController {
  static targets = ["dropZone", "fileInput", "errorMessage"];

  static values = {
    acceptMime: { type: String, default: "application/pdf" },
    maxBytes:   { type: Number, default: 10 * 1024 * 1024 },
  };

  // ---------------------------------------------------------------------------
  // Drag and drop
  // ---------------------------------------------------------------------------

  handleDragOver(event) {
    event.preventDefault();
    if (this.hasDropZoneTarget) {
      this.dropZoneTarget.style.background = "var(--bg)";
    }
  }

  handleDragLeave(event) {
    event.preventDefault();
    if (this.hasDropZoneTarget) {
      this.dropZoneTarget.style.background = "var(--surface)";
    }
  }

  handleDrop(event) {
    event.preventDefault();
    if (this.hasDropZoneTarget) {
      this.dropZoneTarget.style.background = "var(--surface)";
    }
    const file = event.dataTransfer?.files?.[0];
    if (file) this._processFile(file);
  }

  handleSelect(event) {
    const file = event.target?.files?.[0];
    if (file) this._processFile(file);
  }

  // ---------------------------------------------------------------------------
  // Validation + dispatch
  // ---------------------------------------------------------------------------

  _processFile(file) {
    this._clearError();

    if (!this._isAllowedMime(file)) {
      this._fail("invalid_mime", `Format non supporté. Attendu : ${this.acceptMimeValue}.`);
      return;
    }

    if (file.size > this.maxBytesValue) {
      const maxMo = Math.round(this.maxBytesValue / (1024 * 1024));
      this._fail("file_too_large", `Fichier trop volumineux. Taille max : ${maxMo} Mo.`);
      return;
    }

    this.dispatch("selected", { detail: { file } });
  }

  _isAllowedMime(file) {
    if (!this.acceptMimeValue) return true;
    return file.type === this.acceptMimeValue;
  }

  _fail(code, message) {
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.textContent = message;
      this.errorMessageTarget.hidden = false;
    }
    this.dispatch("error", { detail: { code, message } });
  }

  _clearError() {
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.textContent = "";
      this.errorMessageTarget.hidden = true;
    }
  }
}
